import { PureQueryOptions, RefetchQueriesFunction, useLazyQuery, useMutation } from '@apollo/client';
import Link from '@blackbird/ui-base/link';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, Tab, Tabs } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { useTheme } from '@mui/material/styles';
import * as Schema from 'generated/graphql/schema';
import { TFunction } from 'i18next';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { ValidatorForm } from 'react-form-validator-core';
import { makeStyles } from 'tss-react/mui';

import { MutationButton } from '@/components/buttons/mutation-button';
import DialogHeader from '@/components/dialogs/dialog-header';
import DiscardChangesDialog from '@/components/dialogs/discard-changes-dialog';
import { deviceUpdatePeripheral } from '@/graphql/mutations';
import { sensorSettings } from '@/graphql/queries';
import { isWidthDown, isWidthUp } from '@/hocs/with-width';
import { useIAM, useTranslation, useURLQuery, useWidth } from '@/hooks';
import * as Types from '@/types';
import * as BasicInfo from '@/views/dialogs/sensor-settings/tabs/basic-info';
import * as Camera from '@/views/dialogs/sensor-settings/tabs/camera';
import * as CameraAttachment from '@/views/dialogs/sensor-settings/tabs/camera-attachment';
import * as Data from '@/views/dialogs/sensor-settings/tabs/data';
import * as Line from '@/views/dialogs/sensor-settings/tabs/line';
import * as MissingAlarms from '@/views/dialogs/sensor-settings/tabs/missing-alarms';
import * as Sensor from '@/views/dialogs/sensor-settings/tabs/sensor';
import * as Speeds from '@/views/dialogs/sensor-settings/tabs/speeds';
import * as Mapping from '@/views/dialogs/sensor-settings/tabs/stop-mapping/stops-mapping-tab';
import * as Stops from '@/views/dialogs/sensor-settings/tabs/stops';

import { TabPropsType } from './tabs';

const useStyles = makeStyles()((theme) => ({
  tabs: {
    '&[data-rwt-vertical="true"]': {
      display: 'flex',
    },
    display: 'inline-block',
  },
  tabList: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    flexGrow: 0,
    borderRight: '1px solid #ddd',
    marginRight: '1rem',
    fontSize: '0.8rem',
  },
  tabPanel: {
    padding: '20px',
    minHeight: '350px',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      width: '70%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '60%',
    },
  },
  dialogContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  textFields: {
    width: '100%',
  },
  selectFields: {
    width: '100%',
    textAlign: 'left',
  },
  containerClass: {
    width: '100%',
    display: 'block',
    textAlign: 'center',
    marginLeft: '10px',
    paddingRight: '20px',
  },
  discreteContainer: {
    width: '100%',
    textAlign: 'center',
    marginLeft: '10px',
    paddingRight: '20px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  discreteAddButton: {
    flex: '0 0 auto',
    position: 'relative',
    bottom: '10px',
  },
  containerClassTwoColumns: {
    width: '45%',
    display: 'block',
    textAlign: 'center',
    marginLeft: '10px',
    paddingRight: '20px',
  },
  dialogPaper: {
    minHeight: '522px',
    maxHeight: '90vh',
    [theme.breakpoints.down('md')]: {
      minHeight: '100%',
      minWidth: '100%',
      margin: 0,
      borderRadius: 0,
    },
  },
  centeredContainer: {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    alignItems: 'center',
  },
  checkboxField: {
    marginLeft: '0px',
  },
  dialogContent: {
    [theme.breakpoints.down('md')]: {
      padding: '0 0 24px 0px',
    },
  },
  createLineButton: {
    marginRight: '30px',
    marginLeft: '30px',
  },
  assignLineButton: {
    marginRight: '30px',
    marginLeft: '30px',
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  tab: {
    alignItems: 'start',
    border: 0,
    textAlign: 'left',
    color: 'black',
    padding: '0.8rem 2rem',
    margin: '0.2rem 0',
    width: '100%',
    transition: 'background 0.3s cubic-bezier(0.22, 0.61, 0.36, 1)',
    [theme.breakpoints.down('sm')]: {
      padding: '0.8rem',
    },
    '&:hover': {
      outline: 0,
      backgroundColor: '#f4f4f4',
    },
    borderLeft: '2px',
    borderColor: 'transparent',
    borderStyle: 'solid',
  },
  invalidTab: {
    borderColor: theme.palette.error.main,
  },
}));

export type UpdateConfigInput = {
  sensor?:
    | (Omit<Schema.SensorConfigInput, 'wiring'> & {
        wiring?: Omit<Schema.SensorWiringState, '__typename'> | null;
      })
    | null;
  camera?: Schema.CameraConfigInput | null;
};
export type SensorSettingsClasses = ReturnType<typeof useStyles>['classes'];
export type SensorSettingsState = {
  hasChanges: boolean;
  config: UpdateConfigInput | null;
  discardDialogOpen: boolean;
  triedToSave: boolean;
  name: string;
  peripheralType: Schema.PeripheralType;
  description: string;
};

export interface Properties {
  dialogOpen: boolean;
  handleDialogChange: (open: boolean) => void;
  peripheralId: string | null;
  refetchQueries?: Array<string | PureQueryOptions> | RefetchQueriesFunction;
}

const BaseTab =
  (
    state: SensorSettingsState,
    peripheral: Schema.SensorSettingsQuery['peripheralByID'],
    t: TFunction,
  ): React.FC<TabPropsType> =>
  ({ label, isShown, isValid }: TabPropsType) => {
    // This lint is wrong, this callback returns a react component
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { classes, cx } = useStyles();
    if (isShown?.(state, peripheral) === false) {
      return null;
    }
    const valid = !isValid || isValid(state, peripheral);
    return <Tab classes={{ root: classes.tab }} className={cx({ [classes.invalidTab]: !valid })} label={label(t)} />;
  };

const SensorSettingsDialog: FunctionComponent<Properties> = (props) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [config, setConfig] = useState<UpdateConfigInput | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [triedToSave, setTriedToSave] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [peripheralType, setPeripheralType] = useState(Schema.PeripheralType.SENSOR);
  const collectedState = { hasChanges, config, discardDialogOpen, triedToSave, name, description, peripheralType };

  const { t } = useTranslation(['alarms', 'device', 'line', 'shared', 'sensor']);
  const { classes } = useStyles();
  const theme = useTheme();
  const width = useWidth();
  const IAM = useIAM();
  const formRef = useRef<ValidatorForm | null>(null);

  const updateStateAfterFetchingData = (data: Schema.SensorSettingsQuery): void => {
    if (data?.peripheralByID) {
      setName(data.peripheralByID.name);
      setDescription(data.peripheralByID.description);
      setConfig(sensorConfigToUpdateItem(data));
      setPeripheralType(data.peripheralByID.peripheralType);
    }
  };

  const [getSettings, { data, loading, error }] = useLazyQuery<
    Schema.SensorSettingsQuery,
    Schema.SensorSettingsQueryVariables
  >(sensorSettings, {
    variables: { peripheralId: props.peripheralId || '' },
    fetchPolicy: 'cache-and-network',
    onCompleted: updateStateAfterFetchingData,
  });
  const [updateSensorMut, { loading: loadingMut }] = useMutation<
    Schema.DeviceUpdatePeripheralMutation,
    Schema.DeviceUpdatePeripheralMutationVariables
  >(deviceUpdatePeripheral, { refetchQueries: props.refetchQueries });

  const handleChange = (
    newConfig: Partial<UpdateConfigInput>,
    meta?: {
      name?: string;
      description?: string;
    },
  ) => {
    const mergedConfig = {
      ...config,
      ...newConfig,
      sensor: config?.sensor || newConfig?.sensor ? { ...config?.sensor, ...newConfig?.sensor } : null,
      camera: config?.camera || newConfig?.camera ? { ...config?.camera, ...newConfig?.camera } : null,
    };

    setConfig(mergedConfig);
    if (meta) {
      if (typeof meta.name === 'string') {
        setName(meta.name);
      }
      if (typeof meta.description === 'string') {
        setDescription(meta.description);
      }
    }
    setHasChanges(true);
  };

  // Fetch data on dialog open
  useEffect(() => {
    if (
      props.dialogOpen &&
      props.peripheralId !== null &&
      typeof props.peripheralId !== 'undefined' &&
      props.peripheralId !== ''
    ) {
      setConfig(null);
      setHasChanges(false);
      getSettings({ variables: { peripheralId: props.peripheralId } });
    }
  }, [props.dialogOpen]);

  // Update state when new data is fetched
  useEffect(() => {
    if (data?.peripheralByID) {
      setName(data.peripheralByID.name);
      setDescription(data.peripheralByID.description);
      setConfig(sensorConfigToUpdateItem(data));
      setHasChanges(false);
      setPeripheralType(data.peripheralByID.peripheralType);
    }
  }, [data]);

  const { tab } = useURLQuery();

  const hideLineTab = Types.isSensor(data?.peripheralByID)
    ? data?.peripheralByID && data?.peripheralByID.peripheralInformation === undefined
    : true;

  const hideMissingAlarmsTab =
    peripheralType !== 'SENSOR' ||
    config?.sensor?.type === Schema.SensorType.MANUAL_PROCESS ||
    !Types.isSensor(data?.peripheralByID);

  const isEnergyMeter = config?.sensor?.energyMeter === true;

  const invalidFormMessage: string | null = null;
  const enumTypes = {
    analogInputRanges: data?.analogInputRanges?.analogInputRanges ?? [],
    units: data?.units?.units ?? [],
    sensorTypes: data?.sensorTypes?.sensorTypes ?? [],
  };

  const tabProps = { state: collectedState, t, classes };
  const tabPanelProps = { ...tabProps, handleChange, invalidFormMessage };

  const DefaultTab = BaseTab(collectedState, data?.peripheralByID, t);

  const makeTabs = (peripheralId: string) => [
    {
      key: 'basicInfo',
      label: t(['device:basicInformation'], { defaultValue: 'Basic information' }),
      tab: <DefaultTab {...BasicInfo.TabProps} />,
      component: () => <BasicInfo.TabPanel enumTypes={enumTypes} {...tabPanelProps} />,
    },
    {
      key: 'camera',
      label: t(['device:cameraSettings'], { defaultValue: 'Camera' }),
      tab: <DefaultTab {...Camera.TabProps} />,
      component: () => <Camera.TabPanel {...tabPanelProps} />,
    },
    ...(Types.isCamera(data?.peripheralByID)
      ? [
          {
            key: 'cameraAttachment',
            label: t(['device:cameraSensors'], { defaultValue: 'Attached sensors' }),
            tab: <DefaultTab {...CameraAttachment.TabProps} />,
            component: () => (
              <CameraAttachment.TabPanel
                {...tabPanelProps}
                peripheral={data?.peripheralByID! as Partial<Schema.Camera>}
              />
            ),
          },
        ]
      : []),
    ...(Types.isSensor(data?.peripheralByID)
      ? [
          {
            key: 'sensor',
            label: t(['device:sensorSetup'], { defaultValue: 'Sensor' }),
            tab: <DefaultTab {...Sensor.TabProps} />,
            component: () => (
              <Sensor.TabPanel
                {...tabPanelProps}
                sensor={data?.peripheralByID! as Partial<Schema.Sensor>}
                enumTypes={enumTypes}
              />
            ),
          },
        ]
      : []),
    ...(isEnergyMeter
      ? []
      : [
          {
            key: 'speeds',
            label: t(['device:speeds'], { defaultValue: 'Speeds' }),
            tab: <DefaultTab {...Speeds.TabProps} />,
            component: () => <Speeds.TabPanel {...tabPanelProps} />,
          },
        ]),
    {
      key: 'data',
      label: t(['device:dataSetup'], { defaultValue: 'Data' }),
      tab: <DefaultTab {...Data.TabProps} />,
      component: () => <Data.TabPanel {...tabPanelProps} enumTypes={enumTypes} />,
    },
    ...(isEnergyMeter
      ? []
      : [
          {
            key: 'stops',
            label: t(['device:stopSetup'], { defaultValue: 'Stops' }),
            tab: <DefaultTab {...Stops.TabProps} />,
            component: () => <Stops.TabPanel {...tabPanelProps} />,
          },
          {
            key: 'mapping',
            label: t(['device:stopsMapping'], { defaultValue: 'Stops mapping' }),
            tab: <DefaultTab {...Mapping.TabProps} />,
            component: () => <Mapping.StopMappingTab {...tabPanelProps} peripheralId={peripheralId} />,
          },
        ]),
    ...(hideLineTab
      ? []
      : [
          {
            key: 'line',
            label: t(['device:lineSetup'], { defaultValue: 'Line' }),
            tab: <DefaultTab {...Line.TabProps} />,
            component: () => <Line.TabPanel {...tabPanelProps} peripheral={data?.peripheralByID!} />,
          },
        ]),
    ...(hideMissingAlarmsTab
      ? []
      : [
          {
            key: 'missingAlarms',
            label: t(['device:missingDataAlarms'], { defaultValue: 'Missing data alarms' }),
            tab: <DefaultTab {...MissingAlarms.TabProps} />,
            component: () => <MissingAlarms.TabPanel {...tabPanelProps} stateConfigInput={config?.sensor} />,
          },
        ]),
  ];

  const tabsMeta = props.peripheralId ? makeTabs(props.peripheralId) : [];

  const selectedTab = Math.max(
    tabsMeta.findIndex((t) => t?.key === tab),
    0,
  );

  const { query } = useRouter();

  const tabs = useMemo(
    () =>
      tabsMeta.map(({ key, tab }) => (
        <Link key={key} href={{ query: { ...query, tab: key } }}>
          {tab}
        </Link>
      )),
    [tabsMeta],
  );

  //////////////////////
  ///// Return Output
  if (!props.peripheralId) {
    return null;
  }

  const { component: TabContent } = tabsMeta[selectedTab];

  const sensorConfigToUpdateItem = (
    config: Types.OmitRecursively<Schema.SensorSettingsQuery, '__typename'>,
  ): UpdateConfigInput => {
    const peripheral = config.peripheralByID;

    if (Types.isSensor(peripheral)) {
      const config = peripheral.config;
      const dataAlarm = peripheral.config.dataAlarm;
      const discreteConfig = peripheral.config.discreteConfig;
      const accumulatorConfig = peripheral.config.accumulatorConfig;
      return {
        sensor: {
          ...config,
          dataAlarm,
          discreteConfig,
          accumulatorConfig,
          sensorUnit: {
            unit: peripheral.config.sensorUnit?.unit,
            label: peripheral.config.sensorUnit?.label || '',
          },
        },
        camera: null,
      };
    } else if (Types.isCamera(peripheral)) {
      return {
        camera: {
          retentionPeriodHours: (30).days / (1).hours, // TODO: Not implemented in backend
        },
        sensor: null,
      };
    } else {
      throw new Error('Unable to determince sensor type in sensor settings dialog!');
    }
  };

  const handleReset = () => {
    setHasChanges(false);
    if (data?.peripheralByID) {
      setDiscardDialogOpen(false);
      setConfig(sensorConfigToUpdateItem(data));
    } else {
      setDiscardDialogOpen(false);
      setConfig(null);
    }
    props.handleDialogChange(false);
  };

  const handleCloseAndReset = (discard?: boolean) => () => {
    if (hasChanges && !discard) {
      setDiscardDialogOpen(true);
    } else {
      handleReset();
    }
  };

  const handleDiscardResponse = (discard: boolean) => {
    if (discard) {
      handleReset();
    } else {
      setDiscardDialogOpen(false);
    }
  };

  const handleClose = () => {
    handleCloseAndReset(false)();
  };

  const notImplemented = () => ({});

  const onDiscard = () => handleCloseAndReset(true)();

  const onUpdateDeviceConf = async () => {
    if (await isFormValid()) {
      const input = config;
      const { sensor, camera } = input || {};
      const { wiring, ...remainingConfig } = sensor || {};

      const newConfig: Schema.UpdateConfigInput = {
        sensor: { ...remainingConfig, wiring: wiring?.desired || wiring?.reported },
        camera,
      };

      if (newConfig.sensor?.accumulatorConfig) {
        newConfig.sensor.accumulatorConfig = {
          ...newConfig.sensor.accumulatorConfig,
        };
      }

      if (newConfig.sensor?.discreteConfig) {
        newConfig.sensor.discreteConfig = {
          ...newConfig.sensor.discreteConfig,
        };

        // https://github.com/BlackbirdHQ/meta/issues/1874
        // Make sure that we default to a defaultState if nothing is set.
        // The field is required in the UI, so we should never need to do this, but better err on the safe side.
        newConfig.sensor.discreteConfig.defaultState =
          newConfig.sensor.discreteConfig.defaultState ?? Schema.DiscreteDeviceState.OFF;
      }

      if (newConfig.sensor?.dataAlarm) {
        const subscribers = newConfig.sensor?.dataAlarm?.subscribers
          ? [...newConfig.sensor?.dataAlarm?.subscribers]
          : [];
        const newSubscriber = subscribers.filter((subscriber) => {
          if (subscriber.value) {
            return subscriber;
          }
        });
        newConfig.sensor.dataAlarm = {
          ...newConfig.sensor.dataAlarm,
          subscribers: [...newSubscriber],
        };
      }

      if (newConfig.sensor?.sensorUnit) {
        if (!newConfig.sensor.sensorUnit.label && !newConfig.sensor.sensorUnit.unit) {
          newConfig.sensor.sensorUnit = null;
        } else {
          newConfig.sensor.sensorUnit = {
            ...newConfig.sensor.sensorUnit,
          };
        }
      }

      setTriedToSave(false);

      if (!props.peripheralId) {
        return;
      }

      await updateSensorMut({
        variables: {
          peripheralId: props.peripheralId,
          meta: {
            name,
            description,
          },
          config: newConfig,
        },
      });

      getSettings({ variables: { peripheralId: props.peripheralId } });
      handleReset();
    } else {
      // For better user feedback, we tell the user that they are still missing fields, in case they didn't notice the
      // red tab in the left-side.
      setTriedToSave(true);
    }
  };

  const isFormValid = async (): Promise<boolean> => {
    return (await formRef.current?.isFormValid(false)) || false;
  };

  return (
    <Dialog
      open={props.dialogOpen}
      fullWidth
      maxWidth="xl"
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <ValidatorForm ref={formRef} method="post" instantValidate={true} onSubmit={notImplemented}>
        <DialogHeader
          title={t(['sensor:manageSensorSettings'], { defaultValue: 'Manage sensor settings' })}
          onClose={handleClose}
        />
        <DialogContent className={classes.dialogContent}>
          {loading && <LinearProgress />}
          {error && 'Error'}
          {config !== null && (
            <div className={classes.dialogContainer}>
              <Tabs
                indicatorColor="primary"
                value={selectedTab}
                variant={isWidthDown('xl', width) ? 'scrollable' : 'fullWidth'}
                orientation="vertical"
                className={classes.tabList}
              >
                {tabs}
              </Tabs>
              {TabContent()}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onDiscard} color="primary">
            <CloseIcon style={{ marginRight: theme.spacing() }} />
            {isWidthUp('md', width) ? t(['shared:discardChanges'], { defaultValue: 'Discard changes' }) : ''}
          </Button>
          <MutationButton
            color="primary"
            onClick={onUpdateDeviceConf}
            withIcon
            disabled={
              loadingMut ||
              !!(data?.peripheralByID && !IAM.hasPeripheralFeatures(props.peripheralId, ['Devices.Configuration'], 'M'))
            }
            tooltipMsgDisabled={
              loadingMut
                ? undefined
                : t(['device:noPermissionToChangeConfiguration'], {
                    defaultValue: "You don't have permission to change the sensor configuration",
                  })
            }
          >
            <CheckIcon style={{ marginRight: theme.spacing() }} />
            {t(['shared:updateSensorConfiguration'], { defaultValue: 'Update sensor configuration' })}
          </MutationButton>
        </DialogActions>
      </ValidatorForm>
      <DiscardChangesDialog open={discardDialogOpen} handleResponse={handleDiscardResponse} />
    </Dialog>
  );
};

export default SensorSettingsDialog;
